import lottieBuildFromAd from "@v2/components/lottie-build-from-ad.json";
import lottieBuildProductPage from "@v2/components/lottie-build-product-page.json";
import lottieCopyPage from "@v2/components/lottie-copy-page.json";
import lottieReferenceImage from "@v2/components/lottie-reference-image.json";
import lottieRestyle from "@v2/components/lottie-restyle.json";
import lottieSellProduct from "@v2/components/lottie-sell-product.json";
import lottieSuggestIdeas from "@v2/components/lottie-suggest-ideas.json";
import lottieUpdateCopy from "@v2/components/lottie-update-copy.json";

export const PAGE_PLACEHOLDER_OPTIONS = [
  {
    animationData: lottieUpdateCopy,
    lottieClassName: "-ml-1 mt-0",
    title: "Update copy",
    subtitle: "Make text clearer, catchier, or shorter.",
    prompt: `You are an expert copywriter and UX writer. Your job is to improve the copy on my page for clarity, persuasion, and conversion.

Goals
- Make text clearer, catchier, or shorter
- Improve readability and scannability
- Maintain brand voice and tone
- Ensure copy is conversion-focused

Instructions
1. Review all text content on the page
2. Identify areas where copy can be improved
3. Update headlines, subheadlines, body copy, and CTAs
4. Keep the same structure and meaning while improving clarity
5. Explain the reasoning behind each change`,
  },
  {
    animationData: lottieRestyle,
    lottieClassName: "ml-0 mt-0",
    title: "Restyle",
    subtitle: "Change layout, fonts, or colors.",
    prompt: `You are an expert designer and front-end engineer. Restyle my page for clarity, visual hierarchy, and modern aesthetics.

Goals
- Improve visual hierarchy and spacing
- Improve the visual style (aesthetic, eye-capturing, conversion-focused)
- Ensure accessibility (color contrast, readable type scale)
- Preserve functional meaning while enhancing clarity
- Use my design library styles. Make it consistent and systematic

Instructions
1. Identify the major UX issues in the current layout.
2. Update the page and address these UX issues using my design library, ensuring:
   - The page feels balanced and aligned with my brand.
   - There is good information hierarchy
   - The page is visually optimized for conversion
3. Keep all content, copy, and structure intact unless changes are explicitly recommended and explained.

Output Format
- Summary of issues
- Before vs. After rationale`,
  },
  {
    animationData: lottieReferenceImage,
    lottieClassName: "ml-0 mt-0",
    title: "Reference an image",
    subtitle: "Match style, or content of an uploaded image.",
    prompt: `You are an expert designer, UX strategist, and front-end engineer. I will provide you with an image. Use this image as a reference point for improving or generating content for my page.

Goals
- Accurately analyze and interpret the image
- Extract visual style, layout patterns, typography cues, or component structure
- Use the image's aesthetic and layout as inspiration for updating my page
- Maintain consistency between the image and the final webpage content
- Improve clarity, usability, and brand alignment

Instructions
1. Review and interpret the image with attention to:
   - Visual style (colors, spacing, type, mood)
   - Layout (sections, hierarchy, grid)
   - Brand tone or thematic cues
2. Based on the image, produce clear design or content recommendations for my page
3. When updating my page, ensure:
   - The new design is cohesive with the reference image
   - Content is clear, accessible, and conversion-focused
   - The reasoning behind each proposed change is explained
   - You are still using my design library, unless explicitly told otherwise`,
    action: "upload",
  },
  {
    animationData: lottieSellProduct,
    lottieClassName: "-ml-2",
    title: "Sell a product",
    subtitle: "Add a buy box for one of your products.",
    prompt: `You are an expert ecommerce strategist, UX writer, and product-page conversion specialist. Your job is to accurately interpret the product I attached and use it to generate high-quality content for my webpage.

Goals
- Correctly understand all product data (title, description, variants, price, images, etc.)
- Generate a buy box that increases conversion and clarity
- Highlight key differentiators and value propositions
- Maintain consistent brand voice
- Ensure all claims remain truthful to the provided product data

Instructions
1. Review all product data.
2. Use the product information to generate a buy box with:
   - Updated headline, subheadline, and value props
   - Feature/benefit breakdowns
   - Variant/option explanations
   - Product highlights, selling points, and objections answered
   - Merchandising blurbs (e.g., "Why customers love it")
   - Suggested visuals or layout patterns (optional)
3. When creating copy:
   - Keep it concise, scannable, and conversion-focused
   - Use the product's unique selling points from the data—not assumptions
   - Maintain factual correctness at all times`,
    action: "product",
  },
];

export const PLACEHOLDER_OPTIONS = [
  {
    animationData: lottieSuggestIdeas,
    iconClassName: "w-12 h-12 mb-0.5",
    lottieClassName: "w-22 h-20 -ml-6 -mt-4",
    title: "Suggest ideas",
    subtitle:
      "Brainstorm ideas or get template recommendations tailored to your goal.",
    prompt:
      "My goal is: [GOAL]. Recommend page templates optimized for ecommerce that would help me reach this goal. Prioritize templates that drive conversion, highlight products, or support campaigns. Briefly explain how each template helps.",
  },
  {
    animationData: lottieCopyPage,
    iconClassName: "w-12 h-12 mb-0.5",
    lottieClassName: "w-22 h-20 -ml-6 -mt-4",
    title: "Replicate a page",
    subtitle:
      "Paste a URL of a page you love and we'll recreate in your brand styles.",
    prompt: `You are an expert product designer and front-end engineer.

Your task is to visit [insert URL here], analyze the page, and recreate its full layout and all visible UI elements as a structured, production-ready page.

Goals
    •    Faithfully replicate the layout, sections, and components shown on the page
    •    Preserve visual hierarchy, spacing, and relative sizing (not pixel-perfect)
    •    Maintain the intent and function of every element
    •    Ensure accessibility (contrast, readable type scale)
    •    Keep the result clean, systematic, and design-system consistent

Instructions
    1.    Analyze the page at the provided URL
    •    Identify all major sections and layout patterns
    •    Note hierarchy, alignment, spacing, and repeated components
    2.    Rebuild the page
    •    Recreate the page from top to bottom
    •    Include all visible elements (text, buttons, images, cards, nav, etc.)
    •    Match relative proportions and layout logic
    •    Build everything to be interactive and responsive
    3.    Design system & content
    •    Apply my design library styles
    •    Preserve all visible copy exactly as shown
    •    Do not add new content
    •    If something is unclear, make a reasonable inference and note it`,
  },
  {
    animationData: lottieBuildFromAd,
    iconClassName: "w-12 h-12 mb-0.5",
    lottieClassName: "w-20 h-20 -ml-6 -mt-4",
    title: "Build from an ad",
    subtitle:
      "Drop in an ad creative and we'll generate a landing page that matches it.",
    prompt: `Build a high-converting landing page based my attached ad.

The landing page should maintain strong message match with the ad while expanding on its promise with clarity, credibility, and persuasion. Optimize for conversion and skimmability. Assume a modern, performance-driven brand voice: clear, confident, benefit-led, and human.

Use the following structure:

Hero section (message match first)
    •    Mirror the ad's core promise, language, or hook so it feels continuous with what the visitor just clicked
    •    Clarify the outcome and who it's for within the first screen
    •    Include a single, obvious primary CTA aligned with the ad's intent

Value expansion
    •    Expand on the ad's main claim with concrete explanations
    •    Explain why this offer works or is different, without adding new, unrelated promises

Problem → solution framing
    •    Articulate the visitor's pain or motivation implied by the ad
    •    Show how the product or offer directly resolves it

Key benefits
    •    Translate ad-level claims into specific, customer-facing outcomes
    •    Keep benefits concise, scannable, and non-redundant

Credibility & reassurance
    •    Reinforce trust using realistic signals such as process, experience, constraints, guarantees, or careful social-proof language
    •    Do not invent testimonials, metrics, logos, or certifications

Final conversion section
    •    Re-anchor the original ad promise
    •    Add confidence or light urgency without pressure
    •    End with a strong, action-oriented CTA

Writing constraints:
    •    Maintain tight message match with the ad
    •    Avoid marketing clichés and exaggerated claims
    •    No invented facts or social proof
    •    Use clear headers, short paragraphs, and bullets`,
    action: "upload",
  },
  {
    animationData: lottieBuildProductPage,
    iconClassName: "w-12 h-12 mb-0.5",
    lottieClassName: "w-20 h-20 -ml-6 -mt-4",
    title: "Build a product page",
    subtitle:
      "Tag a product you've set up and we'll design a page with all the right context.",
    prompt: `Build a high-converting, branded product page for my product: [@ product here]

Write clear, persuasive page copy optimized for conversion and scannability. Assume a modern ecommerce brand with a confident, benefit-led voice (helpful, not hypey).

Structure the page as follows:

Hero section
Lead with a concise, outcome-focused value proposition
Include a short supporting subhead that clarifies who it's for and why it's different
End with a clear primary CTA

Credibility & trust
Reinforce trust using realistic signals (quality, materials, process, guarantees, or social proof phrased carefully)

Problem → solution narrative
Briefly describe the customer's core pain or frustration
Show how the product directly solves it in a simple, intuitive way

Key benefits
List the most compelling outcomes the customer gets
Prioritize benefits over features
Keep bullets short and skimmable

How it works
Explain how the product is used or experienced in 3–4 simple steps
Focus on ease, speed, and confidence

Objection handling / reassurance
Address common concerns (fit, effectiveness, quality, risk, commitment)
Include FAQs or reassurance statements where appropriate

Final conversion section
Restate the core value proposition
Add a confidence-boosting close (guarantee, reminder of benefits, or light urgency)
End with a strong CTA

Writing constraints:
Avoid generic marketing clichés
No invented facts, testimonials, or claims
Use short paragraphs, bullets, and clear section headers`,
    action: "product",
  },
];
